/**
 * TqTag Form Library
 * Main entry point and public API
 *
 * Client scripts can listen for form events on the form container (or document):
 * - tqtag.form.fieldChange  – when a field value changes. detail: { formId, fieldId, value, element }
 * - tqtag.form.draftSaveStart   – when auto-draft save starts. detail: { formId }
 * - tqtag.form.draftSaveComplete – when auto-draft save finishes. detail: { formId, success, entryId?, error? }
 */

import { FormEngine } from './core/form_engine.js';
import { Security } from './core/security.js';

/**
 * TqTag Form Library
 */
class TqTagForm {
  constructor() {
    this.forms = new Map();
  }

  /**
   * Initialize form
   * @param {string} formId - Form UUID
   * @param {Object} options - Options
   * @returns {Promise<FormEngine>}
   */
  async init(formId, options = {}) {
    if (!Security.validateFormId(formId)) {
      throw new Error('Invalid form ID');
    }

    const container = options.container || document.querySelector(`[data-tqtag-form][data-uuid="${formId}"]`);
    if (!container) {
      throw new Error('Form container not found');
    }

    const engine = new FormEngine(formId, container, options);
    await engine.init();
    
    this.forms.set(formId, engine);
    return engine;
  }

  /**
   * Auto-initialize all forms on page
   */
  autoInit() {
    const formContainers = document.querySelectorAll('[data-tqtag-form]');
    
    formContainers.forEach(container => {
      const formId = container.getAttribute('data-uuid');
      if (formId && Security.validateFormId(formId)) {
        this.init(formId, { container }).catch(() => {});
      }
    });
  }

  /**
   * Get form engine instance
   * @param {string} formId - Form UUID
   * @returns {FormEngine|null}
   */
  getForm(formId) {
    return this.forms.get(formId) || null;
  }
}

// Create global instance
const TqTagFormInstance = new TqTagForm();

// Auto-initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    TqTagFormInstance.autoInit();
  });
} else {
  TqTagFormInstance.autoInit();
}

// Export for module systems
export default TqTagFormInstance;

// Also expose globally for script tag usage
window.TqTagForm = TqTagFormInstance;

